import { ConsentPerPerson } from './controls/ConsentPerPerson.js'
import type { Control } from '@eformulieren/model'
import type { ReactNode } from 'react'
import { Attachments } from './controls/Attachments.js'
import { Criterion } from './controls/Criterion.js'
import { Date } from './controls/Date.js'
import { NumberInput } from './controls/Number.js'
import { RepeatGroup } from './controls/RepeatGroup.js'
import { Checklist, ChipsChoice, Dropdown, Radio } from './controls/Choice.js'
import { Lookup } from './controls/Lookup.js'
import { Placeholder, WAITING_ON } from './controls/Placeholder.js'
import { ReferenceList } from './controls/ReferenceList.js'
import { Richtext } from './controls/Richtext.js'
import { Scale } from './controls/Scale.js'
import { Toggle } from './controls/Toggle.js'
import { Subgrid } from './controls/Subgrid.js'
import { TextArea } from './controls/TextArea.js'
import { TextLine } from './controls/TextLine.js'
import { Verwijzing } from './controls/Verwijzing.js'
import {
  type ControlProps,
  requireDocuments,
  requireConsents,
  requireSingle,
  requireSingleOrText,
  requireFragment,
  requireLaag,
  requireRows,
  requireSection,
  requireVerwijzing,
  requireValues,
} from './controls/kinds.js'

/**
 * De registry: van controltype naar de component die het tekent.
 *
 * Het patroon komt uit de PoC-editor in de wireframe-repo, en dat is vertrekpunt
 * en geen norm (B15): de vorm van de registry is meeneembaar, het datamodel
 * eronder is nieuw. De winst is dat een controltype erbij komen geen wijziging in
 * de schil of in het formulier vraagt (R-22, D-47): één regel hier.
 *
 * Wat er nog niet in staat, staat er met opzet niet in. `controlFor` geeft
 * `undefined` terug, en de aanroeper beslist wat hij daarmee doet; de placeholders
 * met hun reden en hun milestone komen in M2.10. Een lege component teruggeven zou
 * een half werkend veld opleveren dat niemand als gat herkent.
 */

export type ControlRenderer = (props: ControlProps) => ReactNode

export const REGISTRY: Partial<Record<Control, ControlRenderer>> = {
  text: (props) => <TextLine {...requireSingleOrText(props)} soort="text" />,
  textarea: (props) => <TextArea {...requireSingleOrText(props)} />,
  url: (props) => <TextLine {...requireSingle(props)} soort="url" />,
  email: (props) => <TextLine {...requireSingle(props)} soort="email" />,
  phone: (props) => <TextLine {...requireSingle(props)} soort="phone" />,
  number: (props) => <NumberInput {...requireSingle(props)} soort="number" />,
  currency: (props) => <NumberInput {...requireSingle(props)} soort="currency" />,
  date: (props) => <Date {...requireSingle(props)} soort="date" />,
  datetime: (props) => <Date {...requireSingle(props)} soort="datetime" />,
  dropdown: (props) => <Dropdown {...requireSingle(props)} />,
  radio: (props) => <Radio {...requireSingle(props)} />,
  toggle: (props) => <Toggle {...requireSingle(props)} />,
  akkoordPerPersoon: (props) => <ConsentPerPerson {...requireConsents(props)} />,
  checkbox: (props) => <Checklist {...requireValues(props)} />,
  multiselect: (props) => <ChipsChoice {...requireValues(props)} />,
  tag: (props) => <ChipsChoice {...requireValues(props)} />,
  slider: (props) => <Scale {...requireSingle(props)} soort="slider" />,
  rating: (props) => <Scale {...requireSingle(props)} soort="rating" />,
  lookup: (props) => <Lookup {...requireSingle(props)} />,
  richtext: (props) => <Richtext {...requireFragment(props)} />,
  file: (props) => <Attachments {...requireDocuments(props)} soort="file" />,
  image: (props) => <Attachments {...requireDocuments(props)} soort="image" />,
  herhaalgroep: (props) => <RepeatGroup {...requireRows(props)} />,
  subgrid: (props) => <Subgrid {...requireVerwijzing(props)} />,
  // De vormen die nog niet zijn gebouwd, met hun milestone en hun reden erbij
  // (M2.10). Een leeg vak of een half veld leest als een storing.
  ...Object.fromEntries(
    Object.keys(WAITING_ON).map((control) => [control, (props: ControlProps) => <Placeholder {...props} />]),
  ),
  // Ná de plaatshouders, want deze regels horen de wachtregel te overschrijven en niet omgekeerd.
  // `WACHT_OP` draagt `verwijzing` en `criterium` niet meer, dus dit is vandaag geen
  // overschrijving; de volgorde staat er zodat het bij een volgende control die uit de wachtlijst
  // komt niet stil misgaat. Er staat een test op dat een gebouwde control nooit een plaatshouder is.
  verwijzing: (props) => <Verwijzing {...requireVerwijzing(props)} />,
  criterium: (props) => <Criterion {...requireLaag(props)} />,
  // `component` blijft wél in `WACHT_OP` staan (in tegenstelling tot `verwijzing`/`criterium`
  // hierboven): acht van de negen componentwaarden zijn nog niet gebouwd, en deze overschrijving
  // dispatcht per waarde in plaats van het hele controltype vrij te geven.
  component: (props) =>
    'component' in props.veld && props.veld.component === 'referentielijst' ? (
      <ReferenceList {...requireSection(props)} />
    ) : (
      <Placeholder {...props} />
    ),
}

export function controlFor(control: Control): ControlRenderer | undefined {
  return REGISTRY[control]
}

/**
 * Welke controltypes hun kop via `aria-labelledby` krijgen in plaats van via een
 * `label for`.
 *
 * Dat zijn de controls zonder één labelbaar element om naar te wijzen: een groep
 * radioknoppen of aanvinkregels, een schaal van knoppen, en de bewerkbare laag van
 * een rich-text-veld (een `contenteditable`). De kennis staat hier, naast de
 * registry, want hier staat welke component een controltype tekent.
 *
 * `criterium` staat er sinds M7.3 bij, en dat is niet vanzelfsprekend: die control heeft er
 * juist twee, een schaalgroep en een tekstveld. Precies daarom is er geen enkel element dat de
 * vraag kan dragen, en zou een `label for` de kop aan een van de twee vastknopen. Het is
 * gevonden door een test die vroeg of de schaalgroep zijn vraag als naam draagt: zonder deze
 * regel viel `aria-labelledby` terug op het id van de groep zelf, en dan wijst de naam naar de
 * inhoud van de groep in plaats van naar de vraag. De axe-ronde bleef daarbij groen.
 */
export const HEADER_VIA_ARIA: ReadonlySet<Control> = new Set<Control>([
  'radio',
  'checkbox',
  'multiselect',
  'tag',
  'slider',
  'rating',
  'richtext',
  'criterium',
  // Sinds de audit van 30 oktober 2026: de aanvinkregel van een ja/nee-veld krijgt een eigen
  // zichtbare tekst ernaast ("Ja", bij een akkoord "Akkoord") in een `label` om het vinkje, zodat
  // de hele regel het raakvlak is. Een tweede `label for` zou de vraag twee namen geven; de kop
  // hangt er daarom via `aria-labelledby` aan en blijft de naam.
  'toggle',
  // De verklaring staat in de control zelf, net als bij het akkoordveld (eisenboek 12.56).
  'akkoordPerPersoon',
])

export function labelViaHeader(control: Control): boolean {
  return HEADER_VIA_ARIA.has(control)
}

/**
 * De controls die de volle breedte van de sectie krijgen, ook in een sectie met twee kolommen
 * (R-11, ontwerp van het invulscherm, 29 oktober 2026).
 *
 * Twee kolommen zijn er voor korte velden naast elkaar; een lang antwoord in een halve kolom is
 * moeilijk te schrijven en te lezen, en een tabel of een lijst bijlagen past er niet in. Hier en
 * niet in de stylesheet, om dezelfde reden als `HEADER_VIA_ARIA`: de kennis over de controls staat
 * op één plek.
 */
export const FULL_WIDTH: ReadonlySet<Control> = new Set<Control>([
  'richtext',
  'textarea',
  'herhaalgroep',
  'subgrid',
  'component',
  'file',
  'image',
  'criterium',
])

export function fullWidth(control: Control): boolean {
  return FULL_WIDTH.has(control)
}

/**
 * De controls die geen invoer van de invuller vragen: een gegeven uit het dossier, een geplaatst
 * component en het oordeel van de toetslaag. Daar hoort geen sterretje bij, want de lezer vult er
 * niets in (R-09, besluit van 1 november 2026).
 */
export const NOT_INPUT: ReadonlySet<Control> = new Set<Control>(['verwijzing', 'subgrid', 'component', 'criterium'])

export function takesInput(control: Control): boolean {
  return !NOT_INPUT.has(control)
}
